import {
  updateStudentPromoPercentage,
  useStudentReduction,
  useStudentUsers,
  validateStudentStatus,
} from '@gozoki/api'
import { includesLowerCase } from '@gozoki/tools'
import { Button, InputAdornment, Paper, TextField } from '@mui/material'
import { useMutation } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useMemo, useState } from 'react'
import { useToast } from '~/components/communication/Toast'
import { AppUsersHeaderActions } from '~/components/routes/users/app-users-header-actions'
import {
  studentUsersColumns,
  type ValidateUserStatus,
} from '~/components/routes/users/users-columns'
import { UsersPageLayout, UsersSearchGrid } from '~/components/routes/users/users-page-layout'
import { useDeleteSelectedUsers } from '~/components/routes/users/use-delete-selected-users'
import { useRights } from '~/utils/hooks'
import { withRoutePermissions } from '~/utils/rbac'
import { getErrorMessageWithSentry } from '~/utils/sentry'

export const Route = createFileRoute('/_authenticated/users/students')({
  staticData: {
    permission: ['read', 'users'],
  },
  component: StudentUsersPage,
})
withRoutePermissions(Route)

function StudentUsersPage() {
  const isUserSuperAdmin = useRights(['SUPER_ADMIN'])
  const isAdmin = useRights(['ADMIN'])

  const [Toast, showToast] = useToast()
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<number[]>([])
  const [studentPromoPercentage, setStudentPromoPercentage] = useState(0)

  const { data: studentUsers, refetch, isLoading: isLoadingStudentUsers } = useStudentUsers()

  const {
    data: studentPromoPercentageData,
    refetch: refetchStudentPromoPercentage,
    isLoading: isLoadingStudentPromoPercentage,
  } = useStudentReduction()

  useEffect(() => {
    setStudentPromoPercentage(studentPromoPercentageData ?? 0)
  }, [studentPromoPercentageData])

  const users = useMemo(() => studentUsers ?? [], [studentUsers])

  const rows = useMemo(
    () =>
      users.filter((user) =>
        includesLowerCase(`${user.firstName} ${user.lastName} ${user.email}`, search),
      ),
    [users, search],
  )

  const handleDelete = useDeleteSelectedUsers(users, selected, showToast, () => {
    setSelected([])
    return refetch()
  })

  const { mutate: mutateUserStatus } = useMutation({
    mutationFn: ({ userId, status }: ValidateUserStatus) => validateStudentStatus(userId, status),
    onError: (error) => {
      showToast({ message: getErrorMessageWithSentry(error), severity: 'error' })
    },
    onSuccess: () => {
      showToast({ message: "Statut de l'étudiant modifié", severity: 'success' })
      refetch()
    },
  })

  const handleValidateStudentStatus = isAdmin
    ? (u: ValidateUserStatus) => mutateUserStatus({ status: u.status, userId: u.userId })
    : null

  const { mutate: mutateUpdateStudentPromoPercentage } = useMutation({
    mutationFn: updateStudentPromoPercentage,
    onSuccess: async () => {
      await refetchStudentPromoPercentage()
      showToast({ message: 'Promo étudiant mise à jour', severity: 'success' })
    },
    onError: (error) => {
      showToast({ message: getErrorMessageWithSentry(error), severity: 'error' })
    },
  })

  return (
    <UsersPageLayout
      activeTab="students"
      actions={<AppUsersHeaderActions selected={selected} handleDelete={handleDelete} />}
    >
      <Toast />
      {isUserSuperAdmin ? (
        <Paper sx={{ marginTop: '16px' }}>
          <div style={{ alignItems: 'center', display: 'flex', padding: '16px' }}>
            <TextField
              variant="outlined"
              label="Remise étudiante"
              value={studentPromoPercentage}
              onChange={(e) => setStudentPromoPercentage(Number(e.target.value))}
              type="number"
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
            />
            <div style={{ flexGrow: 1 }} />
            <Button
              variant="contained"
              onClick={() => mutateUpdateStudentPromoPercentage(studentPromoPercentage)}
              disabled={isLoadingStudentPromoPercentage}
              sx={{ padding: 3 }}
            >
              Modifier le pourcentage de réduction
            </Button>
          </div>
        </Paper>
      ) : null}
      <UsersSearchGrid
        search={search}
        onSearchChange={setSearch}
        columns={studentUsersColumns(handleValidateStudentStatus)}
        rows={rows}
        loading={isLoadingStudentUsers || isLoadingStudentPromoPercentage}
        checkboxSelection={false}
        disableRowSelectionOnClick
      />
    </UsersPageLayout>
  )
}
